import * as Sentry from '@sentry/react';

import { notify } from '@/actions/ui';
import { SignupSessionError } from '@/auth';
import {
  ErrAddressAlreadyTaken,
  ErrEmailAlreadyExists,
  ErrEmailDomainNotAllowed,
  ErrPhoneAlreadyExists,
} from '@/core/address';
import { APIError, ERROR_CODES, hasErrorCode } from '@/core/api';
import store from '@/store';

// Turns a signup() failure into the form's submit result (ENG-5760). It never
// re-throws, because a rejection escaping react-final-form's onSubmit is an
// unhandled rejection, and it never shows server text it doesn't recognize.
// Unexpected failures go to Sentry as handled errors so they stay visible.
// When the account exists but has no session, it calls onSignInRequired with
// the notice for the sign-in page (signup() has already reported it): a
// resubmit could only fail with "Email address is already in use".
export function handleSignupError(
  e: unknown,
  onSignInRequired: (notice: string) => void
): Record<string, string> | undefined {
  if (e instanceof SignupSessionError) {
    onSignInRequired(e.message);
    return undefined;
  }

  if (hasErrorCode(e, ERROR_CODES.AUTH_EMAIL_ALREADY_EXISTS)) {
    return { email: 'Email address is already in use' };
  }

  // createUser lost a race for the email or phone contact. A retry can't fix
  // it, so it is not reported.
  if (e instanceof APIError && e.message === ErrAddressAlreadyTaken.message) {
    store.dispatch(notify('That email address or phone number is already in use.', 'error'));
    return undefined;
  }

  const message = e instanceof APIError ? e.rawError[0]?.message : undefined;
  switch (message) {
    case ErrEmailAlreadyExists.message:
      return { email: 'Email address is already in use' };
    case ErrPhoneAlreadyExists.message:
      return { phone: 'Phone number is already in use' };
    case ErrEmailDomainNotAllowed.message:
      return { email: 'Email domain not allowed to sign up' };
    case 'invalid first name':
      return { first_name: 'Invalid name' };
    case 'invalid last name':
      return { last_name: 'Invalid name' };
    default:
      Sentry.captureException(e);
      store.dispatch(notify('An error occurred. Please try again.', 'error'));
      return undefined;
  }
}
